import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { MdSchool, MdSearch, MdCompareArrows, MdLocationOn, MdEmojiEvents, MdStar, MdBook, MdSwapHoriz, MdBusiness, MdAttachMoney, MdWork, MdBuild, MdTrendingUp, MdKeyboardArrowDown, MdCode, MdMenuBook, MdArrowOutward, MdArticle, MdSportsEsports, MdMenu, MdClose } from 'react-icons/md';
import CollegeSearchDropdown from './CollegeSearchDropdown';
import CollegeCard from './CollegeCard';
import ImageCarousel from './ImageCarousel';
import StarRating from './StarRating';
import PlacementPieChart from './PlacementPieChart';
import CollegeService from '../services/collegeService';
import { getCollegeLocation, getCollegeName, getCollegeSlug } from '../utils/collegeListing';
import { initializeCompareHandler } from '../utils/compareHandler';
import { getShortCollegeName, resolveCollegeLogo } from '../utils/collegeLogo';

// Dynamic logo loading with Cloudinary fallback
const CollegeLogo = React.memo(({ collegeName, collegeData, style, alt }) => {
  const [logoSrc, setLogoSrc] = useState(null);
  const [showShortName, setShowShortName] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!collegeName) return;

    let cancelled = false;

    const loadLogo = async () => {
      setLoading(true);

      const resolution = await resolveCollegeLogo(collegeName, collegeData?.logo_url);
      if (cancelled) {
        return;
      }

      if (resolution.mode === 'image') {
        setLogoSrc(resolution.src);
        setShowShortName(false);
      } else {
        setLogoSrc(null);
        setShowShortName(true);
      }

      setLoading(false);
    };

    loadLogo();

    return () => {
      cancelled = true;
    };
  }, [collegeName, collegeData?.logo_url]);
  
  if (loading) {
    return (
      <div 
        style={{
          ...style,
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div className="w-4 h-4 border-2 border-gray-300 border-t-purple-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (showShortName) {
    const shortName = getShortCollegeName(collegeName, collegeData?.short_name);
    
    return (
      <div 
        style={{
          ...style,
          backgroundColor: '#6366f1',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: style?.width ? `${parseInt(style.width) * 0.2}px` : '14px',
          fontWeight: 'bold',
          borderRadius: '8px'
        }}
      >
        {shortName}
      </div>
    );
  }
  
  if (!logoSrc) {
    return <div style={{...style, backgroundColor: '#f0f0f0'}} />;
  }
  
  return (
    <img 
      src={logoSrc}
      style={style} 
      alt={alt}
      onError={() => {
        setShowShortName(true);
        setLogoSrc(null);
      }}
    />
  );
});

function CollegeComparator() {
  const [college1, setCollege1] = useState(null);
  const [college2, setCollege2] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [isComparing, setIsComparing] = useState(false);
  const [isInitializingFromUrl, setIsInitializingFromUrl] = useState(true);
  const [demoComparisons, setDemoComparisons] = useState([]);
  const [isLoadingDemoComparisons, setIsLoadingDemoComparisons] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const resultsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const buildComparisonUrl = useCallback((firstCollege, secondCollege) => {
    const firstSlug = firstCollege?.slug;
    const secondSlug = secondCollege?.slug;

    if (!firstSlug || !secondSlug) {
      return null;
    }

    const params = new URLSearchParams();
    params.set('c1', firstSlug);
    params.set('c2', secondSlug);
    return `/comparator?${params.toString()}`;
  }, []);

  // Get real images from MongoDB with optimized quality
  const getCollegeImages = (collegeData, category, count = 4) => {
    const categoryMap = {
      'Location & Campus': 'campus',
      'Infrastructure & Facilities': 'infrastructure', 
      'Academics': 'faculty'
    };
    
    const dbCategory = categoryMap[category];
    const realImages = collegeData?.images?.[dbCategory] || [];
    
    // Convert MongoDB images to required format with quality optimization
    const formattedImages = realImages.slice(0, count).map((img, index) => {
      let optimizedUrl = img?.secure_url || '';
      
      // Add Cloudinary quality optimization if it's a Cloudinary URL
      if (optimizedUrl.includes('cloudinary.com')) {
        optimizedUrl = optimizedUrl.replace('/upload/', '/upload/q_auto:best,f_auto/');
      }
      
      return {
        url: optimizedUrl,
        alt: category === 'Academics' ? `Faculty ${index + 1}` : `${category} ${index + 1}`,
        caption: category === 'Academics' ? `Faculty ${index + 1}` : `${category} View ${index + 1}`,
        loading: 'lazy'
      };
    }).filter(img => img.url); // Remove empty URLs
    
    // If real images found, return only real images
    if (formattedImages.length > 0) {
      return formattedImages;
    }
    
    // Only add default images if NO real images found
    return getDefaultImages(category, 3); // Show only 3 default images for comparator
  };

  // Generate default placeholder images from local assets
  const getDefaultImages = (category, count) => {
    const defaultImageSets = {
      'Location & Campus': [
        '/default-images/campus/default_campus.png'
      ],
      'Infrastructure & Facilities': [
        '/default-images/infrastructure/default_infra1.png',
        '/default-images/infrastructure/default_infra2.png'
      ],
      'Academics': [
        '/default-images/faculty/Default_faculty1.png',
        '/default-images/faculty/Defaulty_faculty2.png'
      ]
    };

    const categoryImages = defaultImageSets[category] || defaultImageSets['Location & Campus'];
    
    return categoryImages.slice(0, count).map((imageUrl, index) => ({
      url: imageUrl,
      alt: category === 'Academics' ? `Faculty ${index + 1}` : `${category} ${index + 1}`,
      caption: category === 'Academics' ? `Faculty ${index + 1}` : `${category} View ${index + 1}`,
      loading: 'lazy',
      isDefault: true
    }));
  };

  const searchColleges = useCallback(async (query) => {
    const response = await CollegeService.searchColleges({ query, limit: 10 });
    return response.data || [];
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadDemoComparisons = async () => {
      setIsLoadingDemoComparisons(true);

      try {
        const response = await CollegeService.getCollegeListings({ page: 1, limit: 80 });
        const colleges = [...(response?.data || [])];
        const pairs = [];
        const shuffledColleges = colleges
          .map((college) => ({ college, sortKey: Math.random() }))
          .sort((a, b) => a.sortKey - b.sortKey)
          .map(({ college }) => college);

        // Build a lightweight preset gallery from a randomized subset instead of rendering all colleges.
        for (let index = 0; index < shuffledColleges.length - 1 && pairs.length < 30; index += 2) {
          const firstCollege = shuffledColleges[index];
          const secondCollege = shuffledColleges[index + 1];

          if (!firstCollege?._id || !secondCollege?._id || firstCollege._id === secondCollege._id) {
            continue;
          }

          const firstSlug = getCollegeSlug(firstCollege);
          const secondSlug = getCollegeSlug(secondCollege);

          if (!firstSlug || !secondSlug) {
            continue;
          }

          pairs.push({
            id: `${firstCollege._id}-${secondCollege._id}`,
            college1: firstCollege,
            college2: secondCollege,
            href: `/comparator?c1=${encodeURIComponent(firstSlug)}&c2=${encodeURIComponent(secondSlug)}&c1id=${encodeURIComponent(firstCollege._id)}&c2id=${encodeURIComponent(secondCollege._id)}`
          });
        }

        if (isActive) {
          setDemoComparisons(pairs);
        }
      } catch (error) {
        console.error('Failed to load demo comparisons:', error);
        if (isActive) {
          setDemoComparisons([]);
        }
      } finally {
        if (isActive) {
          setIsLoadingDemoComparisons(false);
        }
      }
    };

    loadDemoComparisons();

    return () => {
      isActive = false;
    };
  }, []);

  const clearStaleComparisonIfNeeded = useCallback((nextCollege, slot) => {
    if (!comparison) {
      return;
    }

    const comparedCollegeId =
      slot === 'college1'
        ? comparison.college1?.data?._id
        : comparison.college2?.data?._id;

    if (String(nextCollege?._id || '') !== String(comparedCollegeId || '')) {
      setComparison(null);

      if (location.search) {
        navigate('/comparator', { replace: true });
      }
    }
  }, [comparison, location.search, navigate]);

  const handleCollege1Change = useCallback((nextCollege) => {
    setCollege1(nextCollege);
    clearStaleComparisonIfNeeded(nextCollege, 'college1');
  }, [clearStaleComparisonIfNeeded]);

  const handleCollege2Change = useCallback((nextCollege) => {
    setCollege2(nextCollege);
    clearStaleComparisonIfNeeded(nextCollege, 'college2');
  }, [clearStaleComparisonIfNeeded]);

  const compareColleges = useCallback(async (firstCollege = college1, secondCollege = college2, options = {}) => {
    if (!firstCollege?._id || !secondCollege?._id || firstCollege._id === secondCollege._id) return;

    setIsComparing(true);
    try {
      const [college1Data, college2Data] = await Promise.all([
        CollegeService.getCollegeById(firstCollege._id),
        CollegeService.getCollegeById(secondCollege._id)
      ]);

      if (!college1Data || !college2Data) {
        setIsComparing(false);
        return;
      }

      const comparisonData = {
        college1: {
          name: college1Data.college_name,
          data: college1Data
        },
        college2: {
          name: college2Data.college_name,
          data: college2Data
        },
        categories: generateComparisonCategories(college1Data, college2Data)
      };

      setCollege1(college1Data);
      setCollege2(college2Data);
      setComparison(comparisonData);

      const nextUrl = buildComparisonUrl(college1Data, college2Data);
      if (nextUrl && options.updateUrl !== false) {
        navigate(nextUrl, { replace: false });
      }

      setIsComparing(false);
    } catch (error) {
      console.error('Error comparing colleges:', error);
      setIsComparing(false);
    }
  }, [buildComparisonUrl, college1, college2, navigate]);

  const handleCompareNowClick = useCallback(async (selectedCollege) => {
    if (!selectedCollege?._id) return;

    if (!college1) {
      setCollege1(selectedCollege);
    } else if (!college2 && college1._id !== selectedCollege._id) {
      setCollege2(selectedCollege);
      await compareColleges(college1, selectedCollege);
    } else if (college1._id !== selectedCollege._id && college2?._id !== selectedCollege._id) {
      setCollege2(selectedCollege);
      await compareColleges(college1, selectedCollege);
    }
  }, [college1, college2, compareColleges]);

  // Initialize global compare handler
  useEffect(() => {
    initializeCompareHandler(handleCompareNowClick);
  }, [handleCompareNowClick]);

  useEffect(() => {
    let isActive = true;

    const initializeFromUrl = async () => {
      const params = new URLSearchParams(location.search);
      const firstSlug = params.get('c1');
      const secondSlug = params.get('c2');
      const firstId = params.get('c1id');
      const secondId = params.get('c2id');
      const compareId = params.get('compare');

      // Handle single college comparison from card click
      if (compareId && !firstSlug && !secondSlug && !firstId && !secondId) {
        try {
          const selectedCollege = await CollegeService.getCollegeById(compareId);
          if (selectedCollege && isActive) {
            await handleCompareNowClick(selectedCollege);
          }
        } catch (error) {
          console.error('Error loading college for comparison:', error);
        } finally {
          if (isActive) {
            setIsInitializingFromUrl(false);
          }
        }
        return;
      }

      if ((!firstSlug || !secondSlug) && (!firstId || !secondId)) {
        if (isActive) {
          setIsInitializingFromUrl(false);
        }
        return;
      }

      // If the current UI already matches the URL, avoid reloading and re-scrolling.
      if (
        (firstSlug && secondSlug && college1?.slug === firstSlug && college2?.slug === secondSlug) ||
        (firstId && secondId && String(college1?._id || '') === firstId && String(college2?._id || '') === secondId)
      ) {
        if (isActive) {
          setIsInitializingFromUrl(false);
        }
        return;
      }

      try {
        const [firstCollege, secondCollege] = await Promise.all([
          firstSlug ? CollegeService.getCollegeBySlug(firstSlug) : CollegeService.getCollegeById(firstId),
          secondSlug ? CollegeService.getCollegeBySlug(secondSlug) : CollegeService.getCollegeById(secondId)
        ]);

        if (!isActive) {
          return;
        }

        if (!firstCollege || !secondCollege || firstCollege._id === secondCollege._id) {
          setIsInitializingFromUrl(false);
          return;
        }

        setCollege1(firstCollege);
        setCollege2(secondCollege);
        await compareColleges(firstCollege, secondCollege, { updateUrl: false });
      } catch (error) {
        console.error('Error restoring comparison from URL:', error);
      } finally {
        if (isActive) {
          setIsInitializingFromUrl(false);
        }
      }
    };

    initializeFromUrl();

    return () => {
      isActive = false;
    };
  }, [college1?.slug, college2?.slug, compareColleges, handleCompareNowClick, location.search]);

  useEffect(() => {
    if (isInitializingFromUrl) {
      return;
    }

    if (!college1 && !college2 && location.search) {
      navigate('/comparator', { replace: true });
    }
  }, [college1, college2, isInitializingFromUrl, location.search, navigate]);

  useEffect(() => {
    if (!comparison || !resultsRef.current) {
      return;
    }

    requestAnimationFrame(() => {
      const rect = resultsRef.current.getBoundingClientRect();
      const scrollTop = window.scrollY + rect.top - 16;

      window.scrollTo({
        top: Math.max(scrollTop, 0),
        behavior: 'smooth'
      });
    });
  }, [comparison]);

  const generateComparisonCategories = (c1, c2) => {
    return [
      {
        name: "Basic Information",
        icon: MdBusiness,
        items: [
          { label: "Type", c1: c1.type, c2: c2.type },
          { label: "Year Established", c1: c1.year_established, c2: c2.year_established },
          { label: "AICTE Approved", c1: c1.aicte ? "Yes" : "No", c2: c2.aicte ? "Yes" : "No" },
          { label: "UGC Recognized", c1: c1.ugc ? "Yes" : "No", c2: c2.ugc ? "Yes" : "No" },
          { label: "NAAC Grade", c1: c1.naac_grade, c2: c2.naac_grade },
          { label: "Management", c1: c1.management, c2: c2.management }
        ]
      },
      {
        name: "Location & Campus",
        icon: MdLocationOn,
        items: [
          { label: "State", c1: c1.state, c2: c2.state },
          { label: "District", c1: c1.district, c2: c2.district },
          { label: "Location Type", c1: c1.location, c2: c2.location },
          { label: "Address", c1: c1.address, c2: c2.address },
          { label: "Website", c1: c1.website, c2: c2.website },
          { label: "Contact Phone", c1: c1.contact?.phones?.join(', ') || c1.contact?.phone || "N/A", c2: c2.contact?.phones?.join(', ') || c2.contact?.phone || "N/A" },
          { label: "Contact Email", c1: c1.contact?.email || c1.email || "N/A", c2: c2.contact?.email || c2.email || "N/A" },
        ]
      },
      {
        name: "Academics",
        icon: MdSchool,
        items: [
          { label: "University", c1: c1.university_name, c2: c2.university_name },
          { label: "University Type", c1: c1.university_type, c2: c2.university_type },
          { label: "Programs Offered", c1: c1.programs, c2: c2.programs },
          { label: "Specializations", c1: c1.detailed_specializations, c2: c2.detailed_specializations },
          { label: "Course Intake", c1: c1.course_intake, c2: c2.course_intake },
          { label: "Departments", c1: c1.departments, c2: c2.departments }
        ]
      },
      {
        name: "Fees & Admission",
        icon: MdAttachMoney,
        items: [
          { label: "Fee Estimate", c1: c1.fee_estimate, c2: c2.fee_estimate },
          { label: "Admission Process", c1: c1.admission, c2: c2.admission },
          { label: "Eligibility", c1: c1.detailed_eligibility, c2: c2.detailed_eligibility },
          { label: "Scholarships", c1: c1.scholarships, c2: c2.scholarships }
        ]
      },
      {
        name: "Placements",
        icon: MdWork,
        items: [
          { label: "Average Salary", c1: c1.average_salary, c2: c2.average_salary },
          { label: "Highest Salary", c1: c1.highest_salary, c2: c2.highest_salary },
          { label: "Placement Stats", c1: c1.placement_stats, c2: c2.placement_stats },
          { label: "Top Recruiters", c1: c1.top_recruiters, c2: c2.top_recruiters }
        ]
      },
      {
        name: "Infrastructure & Facilities",
        icon: MdBuild,
        items: [
          { label: "Facilities", c1: c1.facilities, c2: c2.facilities },
          { label: "Hostel Details", c1: c1.hostel_details, c2: c2.hostel_details },
          { label: "Clubs & Activities", c1: c1.clubs, c2: c2.clubs },
          { label: "Events & Fests", c1: c1.events_fests, c2: c2.events_fests }
        ]
      },
      {
        name: "Rankings & Reputation",
        icon: MdTrendingUp,
        items: [
          { label: "NAAC Grade", c1: c1.naac_grade, c2: c2.naac_grade },
          { label: "Other Accreditations", c1: c1.other_accreditations?.join(", ") || "None", c2: c2.other_accreditations?.join(", ") || "None" },
          { label: "Student Reviews", c1: c1.student_reviews_ratings, c2: c2.student_reviews_ratings },
          { label: "Notable Alumni", c1: c1.notable_alumni, c2: c2.notable_alumni }
        ]
      }
    ];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-3 sm:p-6 font-sans">
      {/* Mobile drawer backdrop */}<div className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 sm:hidden ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setMenuOpen(false)} />{/* Mobile drawer */}<div className={`fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto bg-white shadow-2xl transition-transform duration-300 ease-in-out sm:hidden ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}><div className="flex items-center justify-between border-b border-gray-200 p-4"><div><span className="text-lg font-bold text-gray-900">Navigation</span><p className="mt-0.5 text-xs text-gray-500">Browse sections</p></div><button type="button" onClick={() => setMenuOpen(false)} className="rounded-lg p-2 hover:bg-gray-100"><MdClose className="h-6 w-6 text-gray-900" /></button></div><div className="p-4"><p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400">Navigation</p><nav className="grid grid-cols-2 gap-2">{[{to:'/tutorials',icon:MdMenuBook,label:'Tutorials'},{to:'/blogs',icon:MdArticle,label:'Blogs'},{to:'/practice-arena',icon:MdSportsEsports,label:'Practice Arena'},{to:'/dashboard',icon:MdSchool,label:'Dashboard'}].map(({to,icon:Icon,label})=>(<Link key={to} to={to} onClick={()=>setMenuOpen(false)} className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium text-white shadow-md" style={{background:'linear-gradient(135deg, #9a51a0, #f36f21)'}}><Icon className="h-3.5 w-3.5 shrink-0" />{label}</Link>))}<button onClick={()=>{setMenuOpen(false);navigate('/compiler',{state:{from:`${location.pathname}${location.search}`}});}} className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-xs font-medium text-white shadow-md" style={{background:'linear-gradient(135deg, #9a51a0, #f36f21)'}}><MdCode className="h-3.5 w-3.5 shrink-0" />Compiler</button></nav></div></div>{/* Header */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-8 mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2 sm:gap-3"><button type="button" onClick={() => setMenuOpen(true)} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-100 sm:hidden"><MdMenu className="h-6 w-6 text-gray-900" /></button><img src="/favicon.png" 
              alt="Logo" 
              className="w-12 h-12 sm:w-16 md:w-20 sm:h-16 md:h-20 object-contain"
            />
            <div>
              <h1 className="text-lg sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-orange-500 bg-clip-text text-transparent leading-normal pb-1">
                College Comparator
              </h1>
              <p className="text-gray-600 text-xs sm:text-sm font-medium mt-0.5">
                Compare colleges side-by-side to make informed decisions
              </p>
            </div>
          </div>
          
            <div className="hidden items-center gap-2 sm:flex sm:gap-3 w-full sm:w-auto">
              <Link
                to="/tutorials"
                className="flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md text-white font-medium text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 flex-1 sm:flex-none"
                style={{ background: 'linear-gradient(135deg, #9a51a0, #f36f21)' }}
              >
                <MdMenuBook className="w-3 h-3 sm:w-4 sm:h-4" />
                Tutorials
              </Link>
              <Link
                to="/blogs"
                className="flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md text-white font-medium text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 flex-1 sm:flex-none"
                style={{ background: 'linear-gradient(135deg, #9a51a0, #f36f21)' }}
              >
                <MdArticle className="w-3 h-3 sm:w-4 sm:h-4" />
                Blogs
              </Link>
              <Link
                to="/practice-arena"
                className="flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md text-white font-medium text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 flex-1 sm:flex-none"
                style={{ background: 'linear-gradient(135deg, #9a51a0, #f36f21)' }}
              >
                <MdSportsEsports className="w-3 h-3 sm:w-4 sm:h-4" />
                Practice Arena
              </Link>
              <button
                onClick={() => navigate('/compiler', { state: { from: `${location.pathname}${location.search}` } })}
                className="flex items-center justify-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md text-white font-medium text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 flex-1 sm:flex-none"
                style={{ background: 'linear-gradient(135deg, #9a51a0, #f36f21)' }}
            >
              <MdCode className="w-3 h-3 sm:w-4 sm:h-4" />
              Compiler
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md text-white font-medium text-xs sm:text-sm transition-all duration-300 hover:shadow-lg hover:scale-105 flex-1 sm:flex-none"
              style={{ background: 'linear-gradient(135deg, #9a51a0, #f36f21)' }}
            >
              Dashboard
            </button>
          </div>
        </div>
      </div>



      {/* Card: College Selection */}
      <div className="bg-white shadow-lg rounded-xl sm:rounded-2xl border border-gray-100 transition-all duration-500 p-4 sm:p-8 mb-6 sm:mb-10">
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="p-2 bg-purple-100 rounded-lg">
            <MdSchool className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
          </div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-800">Select Colleges to Compare</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 items-end transition-all duration-500">
          <div className="space-y-2">
            <label className="text-xs sm:text-sm font-medium text-gray-700 uppercase tracking-wide">First College</label>
            <CollegeSearchDropdown
              onSearch={searchColleges}
              value={college1}
              onChange={handleCollege1Change}
              excludeIds={college2?._id ? [String(college2._id)] : []}
              placeholder="Search and select first college"
              className="w-full p-3 border-2 border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#9a51a0] focus:border-transparent outline-none transition-all text-sm"
            />
          </div>

          <div className="flex justify-center items-center lg:order-2">
            <div className="bg-gradient-to-r from-[#9a51a0] to-[#f36f21] text-white px-4 py-2 rounded-full font-semibold text-sm shadow-md">
              VS
            </div>
          </div>

          <div className="space-y-2 lg:order-3">
            <label className="text-xs sm:text-sm font-medium text-gray-700 uppercase tracking-wide">Second College</label>
            <CollegeSearchDropdown
              onSearch={searchColleges}
              value={college2}
              onChange={handleCollege2Change}
              excludeIds={college1?._id ? [String(college1._id)] : []}
              placeholder="Search and select second college"
              className="w-full p-3 border-2 border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-[#f36f21] focus:border-transparent outline-none transition-all text-sm"
            />
          </div>
        </div>

        {/* Compare Button */}
        <div className={`text-center transition-all duration-500 ${
          comparison ? 'mt-4 sm:mt-6' : 'mt-6 sm:mt-8'
        }`}>
          <button
            onClick={() => compareColleges()}
            disabled={!college1?._id || !college2?._id || college1._id === college2._id || isComparing}
            className="bg-gradient-to-r from-[#9a51a0] to-[#f36f21] text-white px-6 sm:px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 sm:gap-3 mx-auto font-semibold text-sm sm:text-base min-h-[44px]"
          >
            {isComparing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span className="hidden sm:inline">Comparing...</span>
                <span className="sm:hidden">...</span>
              </>
            ) : (
              <>
                <MdCompareArrows className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Compare Colleges</span>
                <span className="sm:hidden">Compare</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Comparison Results */}
      {comparison && (
        <div ref={resultsRef} className="bg-white shadow-xl rounded-xl sm:rounded-2xl overflow-hidden border border-gray-100">
          {/* College Names Header */}
          <div className="bg-gradient-to-r from-purple-50 to-orange-50 p-3 sm:p-5 border-b border-gray-200">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-3 sm:gap-5 items-center">
              <CollegeCard
                college={comparison.college1.data}
                showViewProfile={false}
                className="h-full hover:translate-y-0 hover:shadow-[0_18px_45px_-24px_rgba(15,23,42,0.32)]"
              />
              <div className="flex items-center justify-center">
                <div className="bg-gradient-to-r from-[#9a51a0] to-[#f36f21] text-white px-4 py-2 rounded-full font-semibold text-sm shadow-md">
                  VS
                </div>
              </div>
              <CollegeCard
                college={comparison.college2.data}
                showViewProfile={false}
                className="h-full hover:translate-y-0 hover:shadow-[0_18px_45px_-24px_rgba(15,23,42,0.32)]"
              />
            </div>
          </div>

          {/* Comparison Categories */}
          <div className="p-2 sm:p-4 space-y-2 sm:space-y-3">
            {comparison.categories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <details
                  key={index}
                  open={index < 2}
                  className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg overflow-hidden hover:shadow-sm transition-all duration-300"
                >
                  <summary className="cursor-pointer p-2 sm:p-3 bg-white border-b border-gray-200 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-2">
                      <div className="p-1 bg-purple-100 rounded">
                        <IconComponent className="w-3 h-3 text-purple-600" />
                      </div>
                      <h3 className="text-sm sm:text-base font-medium text-gray-800">{category.name}</h3>
                    </div>
                  </summary>

                  <div className="p-2 sm:p-3">
                    {/* Placement Statistics Pie Charts for Placements category */}
                    {category.name === 'Placements' && (
                      <div className="mb-4">
                        <div className="grid grid-cols-1 gap-2 mb-2 sm:grid-cols-2">
                          <div className="bg-purple-100 rounded p-2 text-center">
                            <h4 className="text-xs sm:text-sm font-semibold text-[#9a51a0] truncate">{comparison.college1.name}</h4>
                          </div>
                          <div className="bg-orange-100 rounded p-2 text-center">
                            <h4 className="text-xs sm:text-sm font-semibold text-[#f36f21] truncate">{comparison.college2.name}</h4>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                          <div className="bg-purple-25 rounded overflow-hidden">
                            <div className="w-full max-w-full overflow-hidden">
                              <PlacementPieChart 
                                placementData={comparison.college1.data.placement_percentage || comparison.college1.data.placement_rate || comparison.college1.data.placement_stats || comparison.college1.data.placement}
                                salaryData={{
                                  average_salary: comparison.college1.data.average_salary || comparison.college1.data.avg_salary || comparison.college1.data.average_package,
                                  highest_salary: comparison.college1.data.highest_salary || comparison.college1.data.max_salary || comparison.college1.data.highest_package || comparison.college1.data.max_package
                                }}
                                className="w-full max-w-none scale-75 sm:scale-90 md:scale-100 origin-center"
                              />
                            </div>
                          </div>
                          <div className="bg-orange-25 rounded overflow-hidden">
                            <div className="w-full max-w-full overflow-hidden">
                              <PlacementPieChart 
                                placementData={comparison.college2.data.placement_percentage || comparison.college2.data.placement_rate || comparison.college2.data.placement_stats || comparison.college2.data.placement}
                                salaryData={{
                                  average_salary: comparison.college2.data.average_salary || comparison.college2.data.avg_salary || comparison.college2.data.average_package,
                                  highest_salary: comparison.college2.data.highest_salary || comparison.college2.data.max_salary || comparison.college2.data.highest_package || comparison.college2.data.max_package
                                }}
                                className="w-full max-w-none scale-75 sm:scale-90 md:scale-100 origin-center"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {['Location & Campus', 'Infrastructure & Facilities', 'Academics'].includes(category.name) && (
                      <div className="mb-3">
                        {/* College Name Headers */}
                        <div className="grid grid-cols-1 gap-2 mb-2 sm:grid-cols-2">
                          <div className="bg-purple-100 rounded p-2 text-center">
                            <h4 className="text-xs sm:text-sm font-semibold text-[#9a51a0]">{comparison.college1.name}</h4>
                          </div>
                          <div className="bg-orange-100 rounded p-2 text-center">
                            <h4 className="text-xs sm:text-sm font-semibold text-[#f36f21]">{comparison.college2.name}</h4>
                          </div>
                        </div>
                        
                        {/* Images Side by Side */}
                        <div className="grid grid-cols-1 gap-2 mb-3 sm:grid-cols-2">
                          <div className="bg-purple-25 rounded">
                            {(() => {
                              const images = getCollegeImages(comparison.college1.data, category.name);
                              const hasRealImages = images.length > 0 && !images[0]?.isDefault;
                              
                              if (hasRealImages) {
                                return (
                                  <ImageCarousel 
                                    images={images} 
                                    title={""}
                                    className="w-full"
                                    autoSlide={false}
                                    slideInterval={6000}
                                    category={category.name}
                                  />
                                );
                              } else if (images.length > 0) {
                                return (
                                  <ImageCarousel 
                                    images={images} 
                                    title={""}
                                    className="w-full"
                                    autoSlide={false}
                                    slideInterval={6000}
                                    category={category.name}
                                    isDefaultImages={true}
                                  />
                                );
                              } else {
                                return (
                                  <div className="aspect-video bg-gray-100 flex items-center justify-center rounded">
                                    <div className="text-center text-gray-400">
                                      <div className="w-8 h-8 bg-gray-300 rounded-full mx-auto mb-2 flex items-center justify-center">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                      <p className="text-xs font-medium">No Images Available</p>
                                    </div>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                          <div className="bg-orange-25 rounded">
                            {(() => {
                              const images = getCollegeImages(comparison.college2.data, category.name);
                              const hasRealImages = images.length > 0 && !images[0]?.isDefault;
                              
                              if (hasRealImages) {
                                return (
                                  <ImageCarousel 
                                    images={images} 
                                    title={""}
                                    className="w-full"
                                    autoSlide={false}
                                    slideInterval={6500}
                                    category={category.name}
                                  />
                                );
                              } else if (images.length > 0) {
                                return (
                                  <ImageCarousel 
                                    images={images} 
                                    title={""}
                                    className="w-full"
                                    autoSlide={false}
                                    slideInterval={6500}
                                    category={category.name}
                                    isDefaultImages={true}
                                  />
                                );
                              } else {
                                return (
                                  <div className="aspect-video bg-gray-100 flex items-center justify-center rounded">
                                    <div className="text-center text-gray-400">
                                      <div className="w-8 h-8 bg-gray-300 rounded-full mx-auto mb-2 flex items-center justify-center">
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                      <p className="text-xs font-medium">No Images Available</p>
                                    </div>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-1">
                      {category.items.map((item, itemIndex) => {
                        const value1 = item.c1;
                        const value2 = item.c2;
                        
                        // Skip if both values are empty/null/undefined
                        if ((!value1 || value1 === "") && (!value2 || value2 === "")) {
                          return null;
                        }
                        
                        return (
                          <div key={itemIndex} className="grid grid-cols-1 gap-2 items-start py-2 border-b border-gray-100 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:gap-2 sm:py-1">
                            <div className="p-2 bg-purple-50 rounded text-xs sm:p-2">
                              <p className="text-gray-600 mb-0.5 font-medium">{item.label}</p>
                              <div className="text-gray-800 font-normal break-words">
                                {(() => {
                                  let displayValue = value1 || "";
                                  
                                  // Special handling for salary fields
                                  if (item.label.toLowerCase().includes('salary') || item.label.toLowerCase().includes('package')) {
                                    if (!value1 || value1 === "" || value1 === null || value1 === undefined) {
                                      displayValue = "Not Available";
                                    }
                                  }
                                  
                                  if (typeof value1 === 'object' && value1 !== null) {
                                    if (Array.isArray(value1)) {
                                      displayValue = value1.join(', ');
                                    } else if (value1.phones || value1.email) {
                                      displayValue = `${value1.phones ? value1.phones.join(', ') : ''} ${value1.email || ''}`;
                                    } else {
                                      displayValue = JSON.stringify(value1);
                                    }
                                  } else if (typeof value1 === 'string' && value1.length > 50) {
                                    displayValue = `${value1.substring(0, 50)}...`;
                                  }
                                  
                                  // Check if this is a rating field and show stars
                                  if (item.label.toLowerCase().includes('rating') || 
                                      item.label.toLowerCase().includes('grade') ||
                                      item.label.toLowerCase().includes('review')) {
                                    const numericRating = parseFloat(displayValue);
                                    if (!isNaN(numericRating) && numericRating >= 0 && numericRating <= 5) {
                                      return (
                                        <div className="w-full">
                                          <div className="flex flex-col gap-1">
                                            <StarRating 
                                              rating={numericRating} 
                                              size="small" 
                                              interactive={false}
                                              showNumeric={false}
                                              className=""
                                            />
                                            <span className="text-xs font-semibold text-gray-700">{displayValue}</span>
                                          </div>
                                        </div>
                                      );
                                    }
                                  }
                                  
                                  return displayValue;
                                })()}
                              </div>
                            </div>
                            <div className="flex items-center justify-center sm:min-h-full">
                              <div className="rounded-full bg-gray-200 p-1">
                                <MdSwapHoriz className="h-3 w-3 text-gray-600 sm:h-2 sm:w-2" />
                              </div>
                            </div>
                            <div className="p-2 bg-orange-50 rounded text-xs sm:p-2">
                              <p className="text-gray-600 mb-0.5 font-medium">{item.label}</p>
                              <div className="text-gray-800 font-normal break-words">
                                {(() => {
                                  let displayValue = value2 || "";
                                  
                                  // Special handling for salary fields
                                  if (item.label.toLowerCase().includes('salary') || item.label.toLowerCase().includes('package')) {
                                    if (!value2 || value2 === "" || value2 === null || value2 === undefined) {
                                      displayValue = "Not Available";
                                    }
                                  }
                                  
                                  if (typeof value2 === 'object' && value2 !== null) {
                                    if (Array.isArray(value2)) {
                                      displayValue = value2.join(', ');
                                    } else if (value2.phones || value2.email) {
                                      displayValue = `${value2.phones ? value2.phones.join(', ') : ''} ${value2.email || ''}`;
                                    } else {
                                      displayValue = JSON.stringify(value2);
                                    }
                                  } else if (typeof value2 === 'string' && value2.length > 50) {
                                    displayValue = `${value2.substring(0, 50)}...`;
                                  }
                                  
                                  // Check if this is a rating field and show stars
                                  if (item.label.toLowerCase().includes('rating') || 
                                      item.label.toLowerCase().includes('grade') ||
                                      item.label.toLowerCase().includes('review')) {
                                    const numericRating = parseFloat(displayValue);
                                    if (!isNaN(numericRating) && numericRating >= 0 && numericRating <= 5) {
                                      return (
                                        <div className="w-full">
                                          <div className="flex flex-col gap-1">
                                            <StarRating 
                                              rating={numericRating} 
                                              size="small" 
                                              interactive={false}
                                              showNumeric={false}
                                              className=""
                                            />
                                            <span className="text-xs font-semibold text-gray-700">{displayValue}</span>
                                          </div>
                                        </div>
                                      );
                                    }
                                  }
                                  
                                  return displayValue;
                                })()}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white shadow-lg rounded-xl sm:rounded-2xl border border-gray-100 transition-all duration-500 p-4 sm:p-8 mb-6 sm:mb-10">
        <div className="mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800">Popular College Comparisons</h2>
          <p className="mt-1 text-sm text-gray-600">
            Explore some quick comparison picks to get started faster.
          </p>
        </div>

        {isLoadingDemoComparisons ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="rounded-2xl border border-gray-200 bg-gray-50 p-4 animate-pulse min-h-[180px]"
              />
            ))}
          </div>
        ) : demoComparisons.length > 0 ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {demoComparisons.map((item) => (
              <article
                key={item.id}
                className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="grid grid-cols-1 gap-3 items-start sm:grid-cols-[1fr_auto_1fr]">
                  <div className="min-w-0 rounded-2xl border border-purple-100 bg-purple-50/60 p-3">
                    <div className="flex items-center gap-3">
                      <CollegeLogo
                        collegeName={getCollegeName(item.college1)}
                        collegeData={item.college1}
                        alt={`${getCollegeName(item.college1)} logo`}
                        style={{
                          width: '48px',
                          height: '48px',
                          objectFit: 'contain',
                          borderRadius: '12px',
                          backgroundColor: '#ffffff',
                          boxShadow: '0 4px 10px rgba(15, 23, 42, 0.08)',
                          padding: '6px'
                        }}
                      />
                      <div className="min-w-0">
                        <h3 className="line-clamp-2 text-sm font-semibold leading-5 text-slate-900">
                          {getCollegeName(item.college1)}
                        </h3>
                        <p className="mt-1 text-xs text-slate-600">
                          {getCollegeLocation(item.college1)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center py-1 sm:h-full sm:py-0">
                    <div className="rounded-full bg-gradient-to-r from-[#9a51a0] to-[#f36f21] px-3 py-2 text-xs font-semibold text-white shadow-md">
                      VS
                    </div>
                  </div>

                  <div className="min-w-0 rounded-2xl border border-orange-100 bg-orange-50/70 p-3">
                    <div className="flex items-center gap-3">
                      <CollegeLogo
                        collegeName={getCollegeName(item.college2)}
                        collegeData={item.college2}
                        alt={`${getCollegeName(item.college2)} logo`}
                        style={{
                          width: '48px',
                          height: '48px',
                          objectFit: 'contain',
                          borderRadius: '12px',
                          backgroundColor: '#ffffff',
                          boxShadow: '0 4px 10px rgba(15, 23, 42, 0.08)',
                          padding: '6px'
                        }}
                      />
                      <div className="min-w-0">
                        <h3 className="line-clamp-2 text-sm font-semibold leading-5 text-slate-900">
                          {getCollegeName(item.college2)}
                        </h3>
                        <p className="mt-1 text-xs text-slate-600">
                          {getCollegeLocation(item.college2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-stretch sm:justify-end">
                  <button
                    onClick={() => {
                      // Set both colleges and trigger comparison
                      setCollege1(item.college1);
                      setCollege2(item.college2);
                      compareColleges(item.college1, item.college2);
                    }}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#9a51a0] to-[#f36f21] px-4 py-3 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:shadow-lg sm:w-auto"
                  >
                    <span>Compare Now</span>
                    <MdArrowOutward className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-600">
            Demo comparisons are not available right now.
          </div>
        )}
      </div>
      
    </div>
  );
}

export default CollegeComparator;
